import React, { useState } from "react";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { validatePublicOfferLetter } from "../services/publicService";
import "./ValidateOfferPage.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ValidateOfferPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    candidateId: "",
    candidateEmail: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [validationPopup, setValidationPopup] = useState(null);

  const validateField = (name, value) => {
    const trimmedValue = value.trim();

    if (name === "candidateId") {
      return trimmedValue ? "" : "Please enter Candidate ID.";
    }

    if (name === "candidateEmail") {
      if (!trimmedValue) {
        return "Please enter Candidate Email.";
      }

      if (!EMAIL_PATTERN.test(trimmedValue)) {
        return "Please enter a valid Candidate Email.";
      }
    }

    return "";
  };

  const getFormErrors = (values) => {
    const nextErrors = {};

    Object.entries(values).forEach(([name, value]) => {
      const message = validateField(name, value);

      if (message) {
        nextErrors[name] = message;
      }
    });

    return nextErrors;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((currentData) => ({
      ...currentData,
      [name]: value,
    }));

    setFieldErrors((currentErrors) => {
      if (!currentErrors[name]) {
        return currentErrors;
      }

      const nextErrors = { ...currentErrors };
      const message = validateField(name, value);

      if (message) {
        nextErrors[name] = message;
      } else {
        delete nextErrors[name];
      }

      return nextErrors;
    });
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const message = validateField(name, value);

    setFieldErrors((currentErrors) => {
      const nextErrors = { ...currentErrors };

      if (message) {
        nextErrors[name] = message;
      } else {
        delete nextErrors[name];
      }

      return nextErrors;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = getFormErrors(formData);

    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      const firstFieldName = Object.keys(nextErrors)[0];

      event.currentTarget
        .querySelector(`[name="${firstFieldName}"]`)
        ?.focus();

      return;
    }

    try {
      const response = await validatePublicOfferLetter({
        candidateId: formData.candidateId.trim(),
        email: formData.candidateEmail.trim(),
      });

      if (response?.success) {
        setValidationPopup({
          variant: "success",
          title: "Validation Successful",
          message: "Offer letter validation completed successfully.",
        });
        return;
      }

      setValidationPopup({
        variant: "error",
        title: "Validation Failed",
        message: response?.message || "Offer letter validation failed.",
      });
    } catch (error) {
      setValidationPopup({
        variant: "error",
        title: "Validation Failed",
        message:
          error?.response?.data?.message || "Offer letter validation failed.",
      });
    }
  };

  const handleCaptchaToggle = () => {
    setIsCaptchaVerified((currentValue) => !currentValue);
  };

  const handleCaptchaKeyDown = (event) => {
    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      handleCaptchaToggle();
    }
  };

  const handleClosePopup = () => {
    setValidationPopup(null);
  };

  const handlePopupAction = () => {
    if (validationPopup?.variant === "error") {
      navigate("/raise-fraud-alert-ticket", {
        state: {
          candidateId: formData.candidateId.trim(),
          candidateEmail: formData.candidateEmail.trim(),
        },
      });
      return;
    }

    handleClosePopup();
  };

  return (
    <div className="validate-offer-page">
      <header className="validate-offer-topbar">
        <div className="validate-offer-topbar-inner">
          <Link to="/" className="validate-offer-brand">
            BlackRoth™
          </Link>

          <nav className="validate-offer-nav" aria-label="Validation navigation">
            <Link
              to="/validate-and-accept-offer"
              className="is-active"
              aria-current="page"
            >
              Validate Offer Letter
            </Link>
            <Link to="/fraud-alert">Recruitment Fraud Alert</Link>
          </nav>
        </div>
      </header>

      <main className="validate-offer-main">
        <Link to="/" className="validate-offer-home-link">
          <ArrowLeft size={16} aria-hidden="true" />
          Back to Home
        </Link>

        <h1 className="validate-offer-page-title">Validate Offer Letter</h1>

        <form className="validate-offer-card" onSubmit={handleSubmit} noValidate>
          <div className="validate-offer-card-header">
            <p>Step 1:</p>
            <h2>Submit details</h2>
          </div>

          <label
            className={`validate-offer-field${
              fieldErrors.candidateId ? " has-error" : ""
            }`}
          >
            <span className="validate-offer-field-label">Candidate ID*</span>
            <input
              type="text"
              name="candidateId"
              autoComplete="off"
              value={formData.candidateId}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.candidateId ? "is-error" : ""}
              aria-invalid={Boolean(fieldErrors.candidateId)}
              aria-describedby={
                fieldErrors.candidateId ? "candidate-id-error" : undefined
              }
            />
            {fieldErrors.candidateId ? (
              <span className="validate-offer-error" id="candidate-id-error">
                {fieldErrors.candidateId}
              </span>
            ) : null}
          </label>

          <label
            className={`validate-offer-field${
              fieldErrors.candidateEmail ? " has-error" : ""
            }`}
          >
            <span className="validate-offer-field-label">Candidate Email*</span>
            <input
              type="email"
              name="candidateEmail"
              autoComplete="email"
              value={formData.candidateEmail}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.candidateEmail ? "is-error" : ""}
              aria-invalid={Boolean(fieldErrors.candidateEmail)}
              aria-describedby={
                fieldErrors.candidateEmail ? "candidate-email-error" : undefined
              }
            />
            {fieldErrors.candidateEmail ? (
              <span className="validate-offer-error" id="candidate-email-error">
                {fieldErrors.candidateEmail}
              </span>
            ) : null}
          </label>

          <div
            className={`validate-offer-captcha${
              isCaptchaVerified ? " is-verified" : ""
            }`}
          >
            <div
              className="validate-offer-captcha-left"
              onClick={handleCaptchaToggle}
              onKeyDown={handleCaptchaKeyDown}
              role="checkbox"
              tabIndex={0}
              aria-checked={isCaptchaVerified}
              aria-label="I am not a robot"
            >
              <span
                className={`validate-offer-captcha-checkbox${
                  isCaptchaVerified ? " is-verified" : ""
                }`}
                aria-hidden="true"
              />
              <div className="validate-offer-captcha-copy">
                <strong>I'm not a robot</strong>
                <span>reCAPTCHA is changing its Terms of Service.</span>
                <button type="button" onClick={(event) => event.stopPropagation()}>
                  Take action
                </button>
              </div>
            </div>

            <div className="validate-offer-captcha-badge" aria-hidden="true">
              <div className="validate-offer-captcha-icon" />
              <span>reCAPTCHA</span>
            </div>
          </div>

          <button
            type="submit"
            className="validate-offer-submit"
            disabled={!isCaptchaVerified}
          >
            SUBMIT
          </button>
        </form>
      </main>

      {validationPopup ? (
        <div
          className="validate-offer-popup-overlay"
          onClick={handleClosePopup}
        >
          <div
            className="validate-offer-popup-card"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={validationPopup.title}
          >
            <div className="validate-offer-popup-header">
              {validationPopup.variant === "success" ? (
                <CheckCircle2
                  className="validate-offer-popup-icon is-success"
                  aria-hidden="true"
                />
              ) : (
                <XCircle
                  className="validate-offer-popup-icon is-error"
                  aria-hidden="true"
                />
              )}
              <h2
                className={`validate-offer-popup-title ${
                  validationPopup.variant === "success"
                    ? "is-success"
                    : "is-error"
                }`}
              >
                {validationPopup.title}
              </h2>
            </div>

            <p className="validate-offer-popup-message">
              {validationPopup.message}
            </p>

            <div className="validate-offer-popup-actions">
              <button
                type="button"
                className="validate-offer-popup-btn"
                onClick={handlePopupAction}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ValidateOfferPage;

 
